import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, ExternalLink, KeyRound } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { useUIStore } from '@/stores/ui-store';
import {
  useConnectIntegration,
  useOAuthUrl,
  type ProviderSummary,
} from '@/hooks/useIntegrations';

interface Props {
  provider: ProviderSummary;
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful connect (api_key / generated_basic). */
  onConnected?: () => void;
}

/**
 * One modal, three shapes:
 *  - oauth:           "Continue to <vendor>" → backend builds the authorize URL
 *  - api_key:         fields declared by the connector → POST /connect
 *  - generated_basic: "Generate credentials" → shows them once with copy buttons
 */
export default function ConnectModal({ provider, isOpen, onClose, onConnected }: Props) {
  const addToast = useUIStore((s) => s.addToast);
  const connect = useConnectIntegration(provider.id);
  const oauthUrl = useOAuthUrl(provider.id);
  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [generated, setGenerated] = useState<Record<string, string> | null>(null);

  function close() {
    setValues({});
    setError('');
    setGenerated(null);
    onClose();
  }

  async function startOAuth() {
    setError('');
    try {
      const { auth_url } = await oauthUrl.mutateAsync({ redirect_to: `/admin/integrations/${provider.id}` });
      window.location.assign(auth_url);
    } catch (err: any) {
      setError(err?.message || 'Could not start the connection');
    }
  }

  async function submitApiKey() {
    setError('');
    for (const f of provider.credential_fields) {
      if (!values[f.key]?.trim()) {
        setError(`${f.label} is required`);
        return;
      }
    }
    try {
      const trimmed = Object.fromEntries(Object.entries(values).map(([k, v]) => [k, v.trim()]));
      await connect.mutateAsync({ credentials: trimmed });
      addToast(`${provider.name} connected`, 'success');
      onConnected?.();
      close();
    } catch (err: any) {
      setError(err?.message || 'Connection failed');
    }
  }

  async function generate() {
    setError('');
    try {
      const res = await connect.mutateAsync({});
      setGenerated(res.credentials ?? null);
      addToast(`${provider.name} credentials generated`, 'success');
      onConnected?.();
    } catch (err: any) {
      setError(err?.message || 'Could not generate credentials');
    }
  }

  function copy(value: string) {
    navigator.clipboard?.writeText(value).then(
      () => addToast('Copied', 'success'),
      () => addToast('Copy failed', 'error'),
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={close} title={`Connect ${provider.name}`}>
      <div className="space-y-4">
        <p className="text-sm text-ink-soft">{provider.description}</p>

        {provider.authKind === 'oauth' && (
          <>
            {!provider.oauth_ready ? (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="font-medium">This platform has not configured {provider.name} OAuth yet.</p>
                <p className="mt-1">
                  Missing: <code className="text-xs">{provider.oauth_missing.join(', ')}</code>. Set them under{' '}
                  <Link to="/admin/settings/credentials" className="underline">Settings → Credentials</Link>.
                </p>
              </div>
            ) : (
              <p className="text-sm text-ink-soft">
                You will be sent to {provider.name} to approve access, then returned here.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={close}>Cancel</Button>
              <Button onClick={startOAuth} isLoading={oauthUrl.isPending} disabled={!provider.oauth_ready}>
                Continue to {provider.name}
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
            {provider.credential_fields.length > 0 && (
              <details className="rounded-lg border border-line p-3">
                <summary className="cursor-pointer text-sm font-medium text-ink-soft">Advanced: connect with an API key instead</summary>
                <div className="mt-3 space-y-3">
                  {provider.credential_fields.map((f) => (
                    <Input
                      key={f.key}
                      label={f.label}
                      type={f.type === 'password' ? 'password' : 'text'}
                      autoComplete="off"
                      placeholder={f.placeholder}
                      helperText={f.help}
                      value={values[f.key] ?? ''}
                      onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                    />
                  ))}
                  <div className="flex justify-end">
                    <Button variant="outline" onClick={submitApiKey} isLoading={connect.isPending}>Connect with key</Button>
                  </div>
                </div>
              </details>
            )}
          </>
        )}

        {provider.authKind === 'api_key' && (
          <>
            <div className="space-y-3">
              {provider.credential_fields.map((f) => (
                <Input
                  key={f.key}
                  label={f.label}
                  type={f.type === 'password' ? 'password' : 'text'}
                  autoComplete="off"
                  placeholder={f.placeholder}
                  helperText={f.help}
                  value={values[f.key] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                />
              ))}
            </div>
            {provider.docsUrl && (
              <a href={provider.docsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-sage-700 underline">
                Where do I find these? <ExternalLink className="h-3 w-3" aria-hidden="true" />
              </a>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={close}>Cancel</Button>
              <Button onClick={submitApiKey} isLoading={connect.isPending}>Connect</Button>
            </div>
          </>
        )}

        {provider.authKind === 'generated_basic' && (
          <>
            {generated ? (
              <div className="space-y-3">
                <p className="text-sm text-ink-soft">
                  Paste these into {provider.name}. You can reveal them again later from the detail page.
                </p>
                {Object.entries(generated).map(([k, v]) => (
                  <div key={k} className="flex items-end gap-2">
                    <Input label={k} readOnly value={v} className="font-mono" />
                    <Button variant="outline" size="md" onClick={() => copy(v)} aria-label={`Copy ${k}`}>
                      <Copy className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                ))}
                <div className="flex justify-end">
                  <Button onClick={close}>Done</Button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-ink-soft">
                  Rumio will create a username and password that you enter in {provider.name}.
                </p>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" onClick={close}>Cancel</Button>
                  <Button onClick={generate} isLoading={connect.isPending}>
                    <KeyRound className="h-4 w-4" aria-hidden="true" /> Generate credentials
                  </Button>
                </div>
              </>
            )}
          </>
        )}

        {error && (
          <p className="text-sm text-red-600" role="alert">{error}</p>
        )}
      </div>
    </Modal>
  );
}
